import { Link } from 'react-router-dom';
import { Building2, ArrowLeft } from 'lucide-react';
import Logo from '../components/Logo';

export default function TermsPage() {
    return (
        <div className="min-h-screen bg-[#FDFEFF]">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-navy-50/50">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <Logo className="w-9 h-9" textClassName="text-xl" />
                    </Link>
                    <Link to="/" className="flex items-center gap-2 text-navy-700 font-medium hover:text-sky-500 transition-colors">
                        <ArrowLeft className="w-4 h-4" />
                        Back to Home
                    </Link>
                </div>
            </nav>

            {/* Content */}
            <main className="pt-32 pb-20 px-6">
                <div className="max-w-4xl mx-auto">
                    <div className="mb-12">
                        <span className="inline-flex items-center px-4 py-1.5 bg-sky-50 text-sky-600 rounded-full text-xs font-bold uppercase tracking-wider mb-6 border border-sky-100">
                            Legal
                        </span>
                        <h1 className="text-4xl sm:text-5xl font-heading font-bold text-navy-900 mb-4">
                            Terms & Conditions
                        </h1>
                        <p className="text-navy-500 text-sm">
                            Last updated: March 1, 2026
                        </p>
                    </div>

                    <div className="prose prose-navy max-w-none space-y-10">
                        {/* Section 1 */}
                        <section>
                            <h2 className="text-2xl font-heading font-bold text-navy-900 mb-4 border-b border-navy-100 pb-3">
                                1. Acceptance of Terms
                            </h2>
                            <p className="text-navy-600 leading-relaxed">
                                By accessing or using EstateCloud ("Platform"), you agree to be bound by these Terms and Conditions ("Terms"). If you do not agree to all of these Terms, you may not access or use the Platform. These Terms apply to all visitors, users, agencies, and others who access or use the service.
                            </p>
                        </section>

                        {/* Section 2 */}
                        <section>
                            <h2 className="text-2xl font-heading font-bold text-navy-900 mb-4 border-b border-navy-100 pb-3">
                                2. Eligibility
                            </h2>
                            <p className="text-navy-600 leading-relaxed">
                                You must be at least 18 years of age to use the Platform. By using this Platform, you represent and warrant that you have the legal capacity to enter into a binding agreement. If you are registering on behalf of a real estate agency or company, you confirm that you have the authority to bind that entity to these Terms.
                            </p>
                        </section>

                        {/* Section 3 */}
                        <section>
                            <h2 className="text-2xl font-heading font-bold text-navy-900 mb-4 border-b border-navy-100 pb-3">
                                3. Account Registration
                            </h2>
                            <p className="text-navy-600 leading-relaxed mb-4">
                                To access certain features, you must register for an account. When creating an account, you agree to:
                            </p>
                            <ul className="list-disc list-inside space-y-2 text-navy-600">
                                <li>Provide accurate, current, and complete information during the registration process.</li>
                                <li>Maintain and promptly update your account information to keep it accurate and complete.</li>
                                <li>Maintain the security and confidentiality of your login credentials.</li>
                                <li>Accept responsibility for all activities that occur under your account.</li>
                                <li>Notify us immediately of any unauthorized access or use of your account.</li>
                            </ul>
                        </section>

                        {/* Section 4 */}
                        <section>
                            <h2 className="text-2xl font-heading font-bold text-navy-900 mb-4 border-b border-navy-100 pb-3">
                                4. Property Listings & RERA Compliance
                            </h2>
                            <p className="text-navy-600 leading-relaxed mb-4">
                                Agencies and users listing properties on EstateCloud are solely responsible for:
                            </p>
                            <ul className="list-disc list-inside space-y-2 text-navy-600">
                                <li>Ensuring the accuracy, legality, and completeness of all property information, images, and documents uploaded.</li>
                                <li>Complying with all applicable Real Estate (Regulation and Development) Act, 2016 (RERA) requirements, including valid RERA registration numbers where applicable.</li>
                                <li>Ensuring no misleading, fraudulent, or deceptive information is published on the Platform.</li>
                                <li>Obtaining all necessary approvals and permissions for property advertisements.</li>
                            </ul>
                            <p className="text-navy-600 leading-relaxed mt-4">
                                EstateCloud does not independently verify the details of individual property listings and shall not be held liable for inaccuracies or misrepresentations in user-submitted content.
                            </p>
                        </section>

                        {/* Section 5 */}
                        <section>
                            <h2 className="text-2xl font-heading font-bold text-navy-900 mb-4 border-b border-navy-100 pb-3">
                                5. Subscriptions & Payments
                            </h2>
                            <p className="text-navy-600 leading-relaxed mb-4">
                                Certain features of the Platform require a paid subscription. By subscribing, you agree to the following:
                            </p>
                            <ul className="list-disc list-inside space-y-2 text-navy-600">
                                <li>Subscription fees are billed in advance on a recurring basis (monthly or annually) depending on the plan selected.</li>
                                <li>All fees are non-refundable unless otherwise stated in a specific offer or required by applicable Indian consumer protection laws.</li>
                                <li>EstateCloud reserves the right to change subscription pricing with at least 30 days' prior notification.</li>
                                <li>Failure to pay may result in suspension or termination of your account and access to premium features.</li>
                            </ul>
                        </section>

                        {/* Section 6 */}
                        <section>
                            <h2 className="text-2xl font-heading font-bold text-navy-900 mb-4 border-b border-navy-100 pb-3">
                                6. User Responsibilities
                            </h2>
                            <p className="text-navy-600 leading-relaxed mb-4">
                                While using EstateCloud, you agree not to:
                            </p>
                            <ul className="list-disc list-inside space-y-2 text-navy-600">
                                <li>Use the Platform for any unlawful purpose or in violation of any applicable law or regulation.</li>
                                <li>Upload, post, or transmit any content that is defamatory, obscene, fraudulent, or infringes on the rights of others.</li>
                                <li>Attempt to gain unauthorized access to other user accounts, the Platform's servers, or any connected systems.</li>
                                <li>Interfere with or disrupt the integrity or performance of the Platform.</li>
                                <li>Scrape, harvest, or collect data from the Platform without prior written consent.</li>
                                <li>Impersonate any person or entity or misrepresent your affiliation with any person or entity.</li>
                            </ul>
                        </section>

                        {/* Section 7 */}
                        <section>
                            <h2 className="text-2xl font-heading font-bold text-navy-900 mb-4 border-b border-navy-100 pb-3">
                                7. Intellectual Property
                            </h2>
                            <p className="text-navy-600 leading-relaxed">
                                All content, features, and functionality of the EstateCloud Platform — including but not limited to text, graphics, logos, icons, images, audio, video, software, and the compilation thereof — are the exclusive property of EstateCloud and are protected by Indian and international copyright, trademark, patent, and trade secret laws. You may not reproduce, distribute, modify, create derivative works of, publicly display, or otherwise exploit any of our proprietary materials without express written permission.
                            </p>
                        </section>

                        {/* Section 8 */}
                        <section>
                            <h2 className="text-2xl font-heading font-bold text-navy-900 mb-4 border-b border-navy-100 pb-3">
                                8. Limitation of Liability
                            </h2>
                            <p className="text-navy-600 leading-relaxed">
                                To the maximum extent permitted by applicable law, EstateCloud, its directors, employees, partners, agents, and affiliates shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or relating to your use of the Platform. This includes, but is not limited to, damages for loss of profits, goodwill, data, or other intangible losses, even if we have been advised of the possibility of such damages. Our total liability shall not exceed the amount paid by you for the service in the twelve (12) months preceding the event giving rise to the claim.
                            </p>
                        </section>

                        {/* Section 9 */}
                        <section>
                            <h2 className="text-2xl font-heading font-bold text-navy-900 mb-4 border-b border-navy-100 pb-3">
                                9. Termination
                            </h2>
                            <p className="text-navy-600 leading-relaxed">
                                We reserve the right to suspend or terminate your account and access to the Platform at our sole discretion, without notice, for conduct that we determine violates these Terms or is harmful to other users, to us, or to third parties, or for any other reason. Upon termination, your right to use the Platform will immediately cease. Any provisions of these Terms that by their nature should survive termination shall remain in effect.
                            </p>
                        </section>

                        {/* Section 10 */}
                        <section>
                            <h2 className="text-2xl font-heading font-bold text-navy-900 mb-4 border-b border-navy-100 pb-3">
                                10. Governing Law & Dispute Resolution
                            </h2>
                            <p className="text-navy-600 leading-relaxed">
                                These Terms shall be governed by and construed in accordance with the laws of India. Any disputes arising out of or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts located in Hyderabad, Telangana, India. Before initiating any formal legal proceeding, both parties agree to first attempt to resolve any dispute through good-faith negotiations.
                            </p>
                        </section>

                        {/* Section 11 */}
                        <section>
                            <h2 className="text-2xl font-heading font-bold text-navy-900 mb-4 border-b border-navy-100 pb-3">
                                11. Changes to These Terms
                            </h2>
                            <p className="text-navy-600 leading-relaxed">
                                EstateCloud reserves the right to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days' notice prior to any new terms taking effect. Your continued use of the Platform after changes become effective constitutes your acceptance of the revised Terms.
                            </p>
                        </section>

                        {/* Section 12 */}
                        <section>
                            <h2 className="text-2xl font-heading font-bold text-navy-900 mb-4 border-b border-navy-100 pb-3">
                                12. Contact Us
                            </h2>
                            <p className="text-navy-600 leading-relaxed">
                                If you have any questions about these Terms, please contact us at:
                            </p>
                            <div className="mt-4 p-6 bg-navy-50/50 rounded-2xl border border-navy-100">
                                <p className="font-semibold text-navy-900 mb-1">EstateCloud</p>
                                <p className="text-navy-600">Email: support@estatecloud.in</p>
                                <p className="text-navy-600">Hyderabad, Telangana, India</p>
                            </div>
                        </section>
                    </div>
                </div>
            </main>

            {/* Footer */}
            <footer className="py-8 px-6 bg-navy-950">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-2">
                            <Building2 className="w-5 h-5 text-sky-500" />
                            <span className="text-sm font-heading font-bold text-white">EstateCloud</span>
                        </div>
                        <div className="flex items-center gap-6 text-sm text-navy-400">
                            <Link to="/privacy" className="hover:text-sky-400 transition-colors">Privacy Policy</Link>
                            <span>© 2026 EstateCloud. All rights reserved.</span>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
